import { ReactNode, useState } from "react";
import { IconButton } from "../IconButton";
import { Icon } from "../Icons";
import { Tooltip } from "../Tooltip";
import "./LabelBlock.css";

/**
 * Props for the LabelBlock component
 */
export interface LabelBlockProps {
  /**
   * Label displayed above the content. Accepts nodes so a label can carry
   * inline content (e.g. a link) as part of the same text run.
   */
  label?: ReactNode;

  /**
   * Whether the block is disabled
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether the block is in an error state
   * @default false
   */
  isError?: boolean;

  /**
   * Whether the block is in a warning state (amber description text)
   * @default false
   */
  isWarning?: boolean;

  /**
   * Width of the block as a percentage
   * @default "100%"
   */
  widthPercent?: string;

  /**
   * Text color for the content (not used directly, but provided for compatibility)
   */
  textColor?: string;

  /**
   * Description content displayed to the right of the field
   */
  description?: ReactNode;

  /**
   * Optional inline adornment rendered next to the label (e.g., loading indicators)
   */
  labelAdornment?: ReactNode;

  /**
   * Tooltip text shown on hover of a "?" icon next to the label.
   * Only rendered when the component is not disabled (i.e., in edit mode),
   * unless `alwaysShowInfoTooltip` is true.
   */
  infoTooltip?: string;

  /**
   * When true, the info tooltip icon is shown even when the component is disabled (display mode).
   * @default false
   */
  alwaysShowInfoTooltip?: boolean;

  /**
   * Additional CSS class for the block
   */
  className?: string;

  /**
   * Removes default padding when true
   * @default false
   */
  noPadding?: boolean;

  /**
   * Content to display inside the block
   */
  children: React.ReactNode;

  /**
   * Additional CSS class for the container
   */
  containerClassName?: string;

  /**
   * Additional CSS class for the content area
   */
  contentClassName?: string;

  /**
   * Optional action menu component (e.g., ThreeDotDropdownMenu)
   * When provided, displays the menu next to the label
   */
  actionMenu?: ReactNode;
  /**
   * External link URL - when provided in display mode, shows a link-out icon button
   */
  externalLink?: string;

  /**
   * Optional control rendered at the trailing (right) edge of the value row —
   * e.g. a "Contact" or "Navigate" button. Aligned right; independent of the
   * label-level `actionMenu`.
   */
  action?: ReactNode;

  /**
   * Optional node rendered inline immediately after the value and before the
   * external-link icon — e.g. a badge qualifying the selected entity. Unlike
   * `action` it stays next to the value rather than being pushed right.
   */
  valueAdornment?: ReactNode;

  /**
   * HTML div element attributes that can be passed to the root element
   */
  [key: string]: unknown;
}

/**
 * LabelBlock Component
 *
 * A flexible layout component that displays a labeled block with optional description.
 * Commonly used for form fields, information displays, and data visualization.
 *
 * Features:
 * - Optional label with error state styling
 * - Focus tracking for interactive elements
 * - Optional description placement
 * - Flexible width control
 * - Customizable padding
 *
 * @example
 * // Basic usage
 * <LabelBlock label="Full Name">
 *   <span>John Doe</span>
 * </LabelBlock>
 *
 * @example
 * // With description
 * <LabelBlock
 *   label="Email Address"
 *   description="We'll never share your email with anyone else."
 * >
 *   <span>john.doe@example.com</span>
 * </LabelBlock>
 *
 * @example
 * // Error state
 * <LabelBlock
 *   label="Password"
 *   isError={true}
 * >
 *   <input type="password" />
 * </LabelBlock>
 */
export function LabelBlock({
  label,
  isError = false,
  isWarning = false,
  disabled = false,
  widthPercent = "100%",
  // textColor parameter kept for compatibility but not used directly
  textColor: _textColor = "text-indigo-text-secondary",
  description,
  labelAdornment,
  infoTooltip,
  alwaysShowInfoTooltip = false,
  children,
  valueAdornment,
  className,
  containerClassName,
  contentClassName,
  noPadding = false,
  actionMenu,
  externalLink,
  action,
  ...additionalProps
}: LabelBlockProps) {
  // Track focus state for styling
  const [isFocused, setIsFocused] = useState(false);

  // Combine CSS classes for main container
  const containerClasses = [
    "indigo-label-block",
    disabled && "indigo-label-block-disabled",
    isError && "indigo-label-block-error",
    isWarning && "indigo-label-block-warning",
    isFocused && "indigo-label-block-focused",
    containerClassName,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  // Combine CSS classes for the label
  const labelClasses = [
    "indigo-label-block-label",
    !noPadding && "indigo-label-block-label-padded",
    isError && "indigo-label-block-label-error",
    isWarning && !isError && "indigo-label-block-label-warning",
  ]
    .filter(Boolean)
    .join(" ");

  // Combine CSS classes for the content area
  const contentClasses = [
    "indigo-label-block-content",
    !noPadding && "indigo-label-block-content-padded",
    label && !noPadding && "indigo-label-block-content-with-label",
    contentClassName,
  ]
    .filter(Boolean)
    .join(" ");

  // Combine CSS classes for the description
  const descriptionClasses = [
    "indigo-label-block-description",
    disabled && "indigo-label-block-description-disabled",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      {...additionalProps}
      className={containerClasses}
      style={{ width: widthPercent, minHeight: "auto" } as React.CSSProperties}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
    >
      {/* Label rendering */}
      {label && (
        <div className="indigo-label-block-label-wrapper">
          <div className="indigo-label-block-label-content">
            <label className={labelClasses}>{label}</label>
            {labelAdornment && (
              <span className="indigo-label-block-label-adornment">
                {labelAdornment}
              </span>
            )}
            {(!disabled || alwaysShowInfoTooltip) && infoTooltip && (
              <Tooltip content={infoTooltip} placement="top-start" delay={200}>
                <span className="indigo-label-block-info-tooltip">
                  <Icon name="functional-question" size="s" />
                </span>
              </Tooltip>
            )}
          </div>

          {/* Optional action menu (e.g., ThreeDotDropdownMenu) */}
          {actionMenu && (
            <div className="indigo-label-block-action-menu">{actionMenu}</div>
          )}
        </div>
      )}

      {/* Content area with optional description */}
      <div className={contentClasses}>
        {/* Main content */}
        <div
          className={
            description
              ? "indigo-label-block-content-with-description"
              : "indigo-label-block-content-full"
          }
        >
          <div className="flex items-center gap-[8px] w-full">
            {children}
            {valueAdornment}
            {externalLink && (
              <IconButton
                iconName="link-out"
                size="sm"
                variant="default"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(externalLink, "_blank", "noopener,noreferrer");
                }}
                tooltip="Open in new tab"
                className="indigo-label-block-external-link"
              />
            )}
            {action && (
              <div className="indigo-label-block-action ml-auto shrink-0">
                {action}
              </div>
            )}
          </div>
        </div>

        {/* Optional description */}
        {description && <div className={descriptionClasses}>{description}</div>}
      </div>
    </div>
  );
}

export default LabelBlock;
